import { useMemo, useState } from "react";
import { Bar, Cell, CartesianGrid, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { api, fmtTime, fmtUSD, safe } from "@/lib/api";
import { ProjectionChart } from "./ProjectionChart";

const DEFAULTS = { growth: 0.02, stress: 0, start: 1 };

function Slider({ id, label, value, min, max, step, onChange, fmt }) {
  return (
    <label className="block">
      <div className="flex justify-between text-xs"><span className="text-slate-600">{label}</span><span className="num text-slate-900" data-testid={`${id}-value`}>{fmt(value)}</span></div>
      <input data-testid={id} type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseFloat(e.target.value))} className="w-full accent-sky-500 mt-1" />
    </label>
  );
}

function ScenarioLab({ task, reload }) {
  const [p, setP] = useState(DEFAULTS);
  const noi = task.metrics.noi.value;
  const ads = task.metrics.ads.value;
  const cap = task.facts.find((f) => f.key === "cap_rate")?.value;
  const rows = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const t = i + 1;
    const base = noi * (1 + p.growth) ** i;
    const n = base * (1 - (t >= p.start ? p.stress : 0));
    return { year: `Y${t}`, noi: n, base_dscr: base / ads, dscr: n / ads, value: cap ? n / cap : null };
  }), [noi, ads, cap, p]);
  if (noi == null || ads == null) return null;
  const y7 = rows[6];
  const minD = Math.min(...rows.map((r) => r.dscr));
  const save = safe(async () => {
    const name = `Growth ${(p.growth * 100).toFixed(1)}% / stress ${(p.stress * 100).toFixed(0)}% from Y${p.start}`;
    await api.post(`/tasks/${task.id}/scenarios`, { name, noi_growth: p.growth, stress_pct: p.stress, stress_start: p.start });
    await reload(); toast.success("Scenario saved to evidence package");
  });
  return (
    <div className="panel" data-testid="scenario-lab">
      <div className="px-4 py-3 border-b border-slate-200 flex justify-between items-center">
        <div><span className="eyebrow">Interactive scenario lab</span><span className="ml-2 text-[10px] text-amber-600">Analyst-adjustable, not sourced forecasts</span></div>
        <div className="flex gap-2">
          <button data-testid="scenario-reset" onClick={() => setP(DEFAULTS)} className="text-[11px] flex items-center gap-1 px-2 py-1 rounded border border-slate-300 text-slate-700"><RotateCcw className="w-3 h-3" />Reset assumptions</button>
          <button data-testid="scenario-save" onClick={save} className="text-[11px] flex items-center gap-1 px-2 py-1 rounded bg-sky-600 text-white hover:bg-sky-700"><Save className="w-3 h-3" />Save to evidence</button>
        </div>
      </div>
      <div className="grid lg:grid-cols-[260px_1fr] gap-4 p-4">
        <div className="space-y-4">
          <Slider id="slider-noi-growth" label="Annual NOI growth" value={p.growth} min={-0.03} max={0.06} step={0.005} onChange={(v) => setP({ ...p, growth: v })} fmt={(v) => `${(v * 100).toFixed(1)}%`} />
          <Slider id="slider-stress" label="Additional NOI stress" value={p.stress} min={0} max={0.4} step={0.01} onChange={(v) => setP({ ...p, stress: v })} fmt={(v) => `${(v * 100).toFixed(0)}%`} />
          <Slider id="slider-stress-start" label="Stress from year" value={p.start} min={1} max={7} step={1} onChange={(v) => setP({ ...p, start: v })} fmt={(v) => `Year ${v}`} />
          <div className="grid grid-cols-2 gap-2 pt-2">
            <div className="bg-slate-50 border border-slate-200 rounded p-2"><div className="eyebrow">Year 7 NOI</div><div className="num text-lg text-slate-900" data-testid="scenario-y7-noi">{fmtUSD(y7.noi, true)}</div></div>
            <div className="bg-slate-50 border border-slate-200 rounded p-2"><div className="eyebrow">Year 7 DSCR</div><div className={`num text-lg ${y7.dscr < 1.25 ? "text-red-600" : "text-slate-900"}`} data-testid="scenario-y7-dscr">{y7.dscr.toFixed(2)}x</div></div>
            <div className="bg-slate-50 border border-slate-200 rounded p-2 col-span-2"><div className="eyebrow">Min DSCR · Debt service</div><div className="num text-sm text-slate-800"><span className={minD < 1.25 ? "text-red-600" : ""}>{minD.toFixed(2)}x</span> · {fmtUSD(ads, true)}/yr</div></div>
          </div>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ left: 4, right: 12 }}>
              <CartesianGrid stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="year" stroke="#475569" fontSize={11} tickLine={false} />
              <YAxis stroke="#475569" fontSize={11} tickLine={false} tickFormatter={(v) => `${v.toFixed(2)}x`} width={52} domain={[0, "auto"]} />
              <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #cbd5e1", fontSize: 12 }} formatter={(v) => `${Number(v).toFixed(2)}x`} />
              <ReferenceLine y={1.25} stroke="#f59e0b" strokeDasharray="4 4" />
              <Bar dataKey="dscr" name="Scenario DSCR" fill="#0ea5e9" radius={[2, 2, 0, 0]}>
                {rows.map((r) => <Cell key={r.year} fill={r.dscr < 1 ? "#ef4444" : r.dscr < 1.25 ? "#f59e0b" : "#0ea5e9"} />)}
              </Bar>
              <Line dataKey="base_dscr" name="Base (no stress)" stroke="#94a3b8" strokeDasharray="4 3" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="overflow-x-auto border-t border-slate-200">
        <table className="w-full text-xs num" data-testid="scenario-table">
          <thead><tr className="text-slate-500 text-left">{["Year", "NOI", "Debt service", "DSCR", "Implied value (NOI / cap)"].map((h) => <th key={h} className="px-4 py-2 font-medium">{h}</th>)}</tr></thead>
          <tbody>{rows.map((r) => <tr key={r.year} className="border-t border-slate-200"><td className="px-4 py-1.5 text-slate-600">{r.year}</td><td className="px-4 py-1.5">{fmtUSD(r.noi)}</td><td className="px-4 py-1.5">{fmtUSD(ads)}</td><td className={`px-4 py-1.5 ${r.dscr < 1.25 ? "text-red-600" : ""}`}>{r.dscr.toFixed(2)}x</td><td className="px-4 py-1.5">{fmtUSD(r.value)}</td></tr>)}</tbody>
        </table>
      </div>
      {task.scenarios.length > 0 && (
        <div className="border-t border-slate-200 p-4" data-testid="saved-scenarios">
          <div className="eyebrow mb-2">Saved to evidence ({task.scenarios.length})</div>
          {task.scenarios.map((s) => <div key={s.id} className="text-xs text-slate-600 flex justify-between py-1"><span>{s.name}</span><span className="num">Y7 {s.y7_dscr.toFixed(2)}x · min {s.min_dscr.toFixed(2)}x · {s.by} · {fmtTime(s.at)}</span></div>)}
        </div>
      )}
    </div>
  );
}

export function Forecast({ task, reload }) {
  if (!task.facts.length) return <div className="p-6 text-sm text-slate-500">Extract facts first.</div>;
  return (
    <div className="p-6 space-y-5" data-testid="forecast-panel">
      <ProjectionChart projection={task.projection} />
      <ScenarioLab task={task} reload={reload} />
    </div>
  );
}
